import { forwardRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';

import { cn } from '../../utils/cn';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export const DialogOverlay = forwardRef(function DialogOverlay(
  { className, ...props },
  ref,
) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-ds-id="ds/overlay/dialog"
      className={cn(
        'fixed inset-0 z-50 grid place-items-center bg-scrim/40 backdrop-blur-sm animate-fade-in',
        className,
      )}
      {...props}
    />
  );
});

export const DialogContent = forwardRef(function DialogContent(
  {
    className,
    children,
    hideClose = false,
    tone = 'default',
    ...props
  },
  ref,
) {
  const destructive = tone === 'destructive';

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-ds-id={
          destructive ? 'ds/overlay/dialog--destructive' : 'ds/overlay/dialog'
        }
        className={cn(
          'fixed left-1/2 top-1/2 z-50 flex w-[480px] max-w-[calc(100%-2rem)] max-h-[min(90dvh,calc(100dvh-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-ds-xl border border-border bg-card p-space-6 shadow-2xl focus:outline-none animate-fade-in space-y-space-4',
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose ? (
          <DialogPrimitive.Close
            className={cn(
              'absolute right-4 top-4 rounded-full p-1 hover:bg-muted',
              destructive
                ? 'text-danger-500 hover:text-danger-700'
                : 'text-muted-foreground/70 hover:text-muted-foreground',
            )}
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});

export const DialogHeader = ({ className, ...props }) => (
  <div className={cn('mb-4 flex flex-col gap-1', className)} {...props} />
);

export const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn('flex justify-end gap-space-3', className)}
    {...props}
  />
);

export const DialogTitle = forwardRef(function DialogTitle(
  { className, ...props },
  ref,
) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn('font-display text-lg font-semibold text-foreground', className)}
      {...props}
    />
  );
});

export const DialogDescription = forwardRef(function DialogDescription(
  { className, ...props },
  ref,
) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
});
